// 099 D03 运营总览：KPI + 环比、TTFV 激活时效、趋势图（运营事件标注）、D09 转化漏斗
import { useMemo, useState } from "react";
import { ops } from "../../lib/api";
import { useOpsQuery } from "./useOpsQuery";
import { DaySeg, Kpi, Note, Sect, fmtMoney, fmtMs, fmtPct, fmtTokens, pctDelta } from "./widgets";

const SVG_W = 700;
const SVG_H = 210;
const X0 = 40;
const X1 = 660;
const Y0 = 20;
const Y1 = 180;

function TrendChart(props: { daily: { date: string; signups: number; jobs: number }[]; events: { id: string; event_date: string; note: string }[] }) {
  const { daily, events } = props;
  const maxV = Math.max(1, ...daily.map((d) => Math.max(d.signups, d.jobs)));
  const x = (i: number) => X0 + (i * (X1 - X0)) / Math.max(1, daily.length - 1);
  const y = (v: number) => Y1 - ((Y1 - Y0) * v) / maxV;
  const line = (key: "signups" | "jobs") =>
    daily.map((d, i) => `${x(i)},${y(d[key])}`).join(" ");
  const eventXs = events
    .map((e) => ({ e, i: daily.findIndex((d) => d.date === e.event_date) }))
    .filter((r) => r.i >= 0);
  return (
    <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: "100%", height: "auto" }} role="img" aria-label="趋势图">
      <line x1={X0} y1={Y0} x2={X0} y2={Y1} stroke="#d2d2cc" />
      <line x1={X0} y1={Y1} x2={X1} y2={Y1} stroke="#0b0b0b" strokeWidth={1.5} />
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={X0} y1={Y1 - (Y1 - Y0) * f} x2={X1} y2={Y1 - (Y1 - Y0) * f} stroke="#e6e6e0" />
      ))}
      <polyline points={line("jobs")} fill="none" stroke="#0b0b0b" strokeWidth={2.5} />
      <polyline points={line("signups")} fill="none" stroke="#ff4d19" strokeWidth={2} strokeDasharray="6 4" />
      {daily.map((d, i) => (
        <rect key={d.date} x={x(i) - 3.5} y={y(d.jobs) - 3.5} width={7} height={7} fill="#0b0b0b" />
      ))}
      {eventXs.map(({ e, i }) => (
        <g key={e.id}>
          <line x1={x(i)} y1={Y0 - 6} x2={x(i)} y2={Y1} stroke="#ff4d19" strokeWidth={1.5} strokeDasharray="4 3" />
          <title>{`${e.event_date}：${e.note}`}</title>
        </g>
      ))}
      <g fontSize={11} fill="#666661" textAnchor="middle">
        {daily.map((d, i) => (
          daily.length <= 10 || i % Math.ceil(daily.length / 10) === 0
            ? <text key={d.date} x={x(i)} y={197}>{d.date.slice(5).replace("-", "/")}</text>
            : null
        ))}
      </g>
      <g fontSize={11.5} fill="#0b0b0b" fontWeight={600}>
        <rect x={X1 - 182} y={SVG_H - 14} width={18} height={3} fill="#0b0b0b" /><text x={X1 - 159} y={SVG_H - 9}>发起生成</text>
        <rect x={X1 - 98} y={SVG_H - 14} width={18} height={3} fill="#ff4d19" /><text x={X1 - 75} y={SVG_H - 9}>新增注册</text>
      </g>
    </svg>
  );
}

export default function Overview() {
  const [days, setDays] = useState(7);
  const ov = useOpsQuery(() => ops.overview(days), [days]);
  const fu = useOpsQuery(() => ops.funnel(days), [days]);
  const [evDate, setEvDate] = useState(() => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Shanghai" }));
  const [evNote, setEvNote] = useState("");
  const [evBusy, setEvBusy] = useState(false);
  const [evErr, setEvErr] = useState<string | null>(null);

  const d = ov.data;
  const funnelMax = useMemo(() => {
    if (!fu.data) return 1;
    return Math.max(1, ...fu.data.stages.map((s) => s.count ?? 0));
  }, [fu.data]);

  if (ov.loading && !d) return <p className="muted">加载中…</p>;
  if (ov.forbidden) return <p>🔒 {ov.forbidden}</p>;
  if (ov.error) return <p style={{ color: "var(--err)" }}>加载失败：{ov.error}</p>;
  if (!d) return null;

  const k = d.kpis;
  const doneRate = k.jobsStarted ? k.done / k.jobsStarted : null;
  const failRate = k.jobsStarted ? k.failed / k.jobsStarted : null;

  return (
    <>
      <h2 className="d-title">运营总览</h2>
      <p className="d-sub2">来了多少人 · 注册多少 · 多少人真正生成 · 哪里失败 · 花了多少</p>

      <div className="d-filter">
        <DaySeg days={days} onChange={setDays} />
        <span className="d-tz">时区 {d.range.timezone} ｜ 区间 [{d.range.since.slice(0, 10)}, {d.range.until.slice(0, 10)})</span>
        <span className="d-updated">更新于 <b>{new Date(d.range.updatedAt).toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", hour12: false })}</b></span>
      </div>

      <div className="d-grid d-kpis">
        <Kpi label="访问用户" value={k.visitors} hint="GA4" naText="未接入 GA4" />
        <Kpi label="浏览量 PV" value={k.pageviews} naText="未接入 GA4" />
        <Kpi label="新增注册" value={k.signups} delta={pctDelta(d.prev.pct.signups).text} deltaKind={pctDelta(d.prev.pct.signups).kind} hint="Auth" />
        <Kpi label="活跃用户" value={k.activeUsers} delta={pctDelta(d.prev.pct.activeUsers).text} deltaKind={pctDelta(d.prev.pct.activeUsers).kind} hint="业务事件" />
        <Kpi label="新建项目" value={k.newProjects} />
        <Kpi label="发起生成" value={k.jobsStarted} delta={pctDelta(d.prev.pct.jobsStarted).text} deltaKind={pctDelta(d.prev.pct.jobsStarted).kind} />
        <Kpi label="完成" value={k.done} delta={doneRate !== null ? `完成率 ${fmtPct(doneRate)}` : "—"} />
        <Kpi label="失败" value={k.failed} kind={k.failed > 0 ? "bad" : undefined} delta={failRate !== null ? `失败率 ${fmtPct(failRate)}` : "—"} deltaKind="down" />
        <Kpi label="取消" value={k.canceled} delta="另列，不计入失败" />
        <Kpi label="运行中" value={k.runningNow} kind={k.runningNow > 0 ? "warn" : undefined} delta="当前快照 · 非区间计数" />
        <Kpi label="Token 消耗" value={fmtTokens(k.tokensIn + k.tokensOut)} delta={`入 ${fmtTokens(k.tokensIn)} / 出 ${fmtTokens(k.tokensOut)}`} />
        <Kpi label="估算费用" value={fmtMoney(k.costCents)} delta={pctDelta(d.prev.pct.costCents).text} deltaKind={pctDelta(d.prev.pct.costCents).kind} />
      </div>
      <Note>
        口径：访问/PV 在 GA4 接入前显示「未接入」；「运行中」为当前状态快照，与区间计数分区；成本以 llm_calls 调用台账为权威（含失败/重试/取消单）；
        内部运营账号建议剔除——当前统计未默认剔除（用户页可查看运营账号标记）。上期为 0 时不显示环比百分比。
      </Note>

      <Sect title="激活时效与趋势" tag="D03" hint="朋友测试核心：多久拿到第一份能用的 PPT" />
      <div className="cols2">
        <div className="d-card">
          <h4 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 800 }}>激活时效（TTFV）· 范围内注册队列，样本 {d.ttfv.sample}</h4>
          <div className="d-tblwrap">
            <table>
              <thead><tr><th>阶段</th><th>中位数</th><th>P95</th></tr></thead>
              <tbody>
                <tr><td>注册 → 创建首个项目</td><td className="num">{fmtMs(d.ttfv.regToFirstProjectMs.median)}</td><td className="num">{fmtMs(d.ttfv.regToFirstProjectMs.p95)}</td></tr>
                <tr><td>首个项目 → 首份完成 PPT</td><td className="num">{fmtMs(d.ttfv.firstProjectToFirstDeckMs.median)}</td><td className="num">{fmtMs(d.ttfv.firstProjectToFirstDeckMs.p95)}</td></tr>
                <tr><td><b>注册 → 首份完成 PPT</b></td><td className="num"><b>{fmtMs(d.ttfv.totalMs.median)}</b></td><td className="num"><b>{fmtMs(d.ttfv.totalMs.p95)}</b></td></tr>
              </tbody>
            </table>
          </div>
          <Note>未完成激活的用户不计入耗时分母（避免虚低）；样本 &lt; 10 时仅作参考。完成时间口径：COALESCE(terminal_at, updated_at)。</Note>
        </div>
        <div className="d-card">
          <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 800 }}>发起生成 / 新增注册 趋势</h4>
          <TrendChart daily={d.daily} events={d.events} />
          <ul className="d-eventlist">
            {d.events.map((e) => <li key={e.id}>📌 <b>{e.event_date.slice(5).replace("-", "/")}</b> {e.note}</li>)}
          </ul>
          <form
            className="d-eventform"
            onSubmit={(ev) => {
              ev.preventDefault();
              if (!evNote.trim()) return;
              setEvBusy(true);
              setEvErr(null);
              ops.addEvent(evDate, evNote.trim())
                .then(() => { setEvNote(""); ov.reload(); })
                .catch((e) => setEvErr((e as Error).message))
                .finally(() => setEvBusy(false));
            }}
          >
            <input type="date" value={evDate} onChange={(e) => setEvDate(e.target.value)} />
            <input type="text" maxLength={200} placeholder="运营事件标注（只写事实，如：邀请第二批朋友 ×5）" value={evNote} onChange={(e) => setEvNote(e.target.value)} />
            <button className="d-chip" type="submit" disabled={evBusy || !evNote.trim()}>{evBusy ? "提交中…" : "＋ 标注"}</button>
          </form>
          {evErr ? <div className="small" style={{ color: "var(--err)" }}>{evErr}</div> : null}
          <Note>标注只写事实与日期，用于解释数据波动；记录填写人。虚线橙线 = 标注日。</Note>
        </div>
      </div>

      <Sect title={`转化漏斗（范围内注册队列 ${fu.data?.cohortSize ?? "…"} 人）`} tag="D09" hint="访客段未接入，仅作 DB 段对账" />
      {fu.data ? (
        <div className="d-card d-funnel">
          {fu.data.stages.map((s, i) => {
            const prev = i > 0 ? fu.data!.stages[i - 1] : null;
            const conv = s.count !== null && prev?.count ? s.count / prev.count : null;
            const gapKeys = ["", "regToProj", "projToJob", "jobToDone"] as const;
            const gapKey = gapKeys[i - 1];
            const gap = gapKey && fu.data ? fu.data.gapMedianMs[gapKey as "regToProj"] : null;
            return (
              <div className="d-frow" key={s.key}>
                <span className="fname">{s.label}</span>
                <div className="fbar-bg">
                  {s.count === null
                    ? <div className="fbar na">{s.note ?? "未接入"}</div>
                    : <div className="fbar" style={{ width: `${Math.max(4, (s.count / funnelMax) * 100)}%` }}>{s.count}</div>}
                </div>
                <span className="fconv">
                  {conv !== null ? <>转化 <b>{fmtPct(conv)}</b></> : (s.note ?? "—")}
                  {gap !== null && gap !== undefined ? <span className="ftime">｜级间中位 {fmtMs(gap)}</span> : null}
                </span>
              </div>
            );
          })}
          <Note>各级使用同一注册队列；GA4 匿名访客与 Auth 账户不做无依据拼接；下载段未埋点，不补造。</Note>
        </div>
      ) : <p className="muted">漏斗加载中…</p>}
    </>
  );
}
